/*
 * Copyright (c) 2025-2026 Aegis AO Soft LLC and Alexander Orlov.
 * 34 Middletown Ave, Atlantic Highlands, NJ 07716
 *
 * THIS SOFTWARE IS THE CONFIDENTIAL AND PROPRIETARY INFORMATION OF
 * Aegis AO Soft LLC and Alexander Orlov.
 *
 * This code may be used, reproduced, modified, or distributed ONLY with the
 * prior written permission of Aegis AO Soft LLC / Alexander Orlov.
 *
 * Author: Alexander Orlov
 * Aegis AO Soft LLC
 */

/**
 * Where to send a user after they sign in. ProtectedRoute bounces a signed-out visitor to /login with
 * `state.from` = the location they asked for; the login page used to ignore it and always land on the
 * dashboard. That broke links that only work for a signed-in owner — above all the Direct Install URL
 * of our Samsara marketplace app (/connect/samsara): a customer arriving from Samsara signed in and
 * never reached the Connect step.
 *
 * Only an in-app path is honoured: it must start with a single "/", so "//evil.example" (protocol-
 * relative) and "https://…" never become an open redirect, and /login itself is ignored to avoid a loop.
 */
export function safeReturnPath(from: unknown): string | null {
  if (!from || typeof from !== 'object') return null;
  const loc = from as { pathname?: unknown; search?: unknown; hash?: unknown };
  if (typeof loc.pathname !== 'string') return null;

  const pathname = loc.pathname;
  if (!pathname.startsWith('/') || pathname.startsWith('//') || pathname.includes('\\')) return null;
  if (pathname === '/' || pathname === '/login' || pathname.startsWith('/login/')) return null;

  const search = typeof loc.search === 'string' && loc.search.startsWith('?') ? loc.search : '';
  const hash = typeof loc.hash === 'string' && loc.hash.startsWith('#') ? loc.hash : '';
  return `${pathname}${search}${hash}`;
}
